import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import {
  useRetrieveOptimizedGantt,
  useRetrieveWbsMeta,
} from "@planzone/view-models";
import { GanttFilter, Sort as S } from "@planzone/dto";
import { events } from "@planzone/events";

import { usePreferences } from "@components/preferences";
import { createHoverStore, HoverProvider } from "@common/elementHover";
import { InfiniteScroll } from "@common/infiniteScroll";
import { TreeToggle } from "@common/treeToggle";
import { useMessage } from "@components/useMessage";
import { Wbs } from "@scenes/wbs";
import { Props } from "./types";
import { Heading } from "./heading";
import { DisplayToggle } from "./displayToggle";
import { Statuses } from "./statuses";
import { CreationMenu } from "./creationMenu";
import { Filters } from "./filters";
import { Sort } from "./sort";
import { ExportButton } from "./exportButton";
import styles from "./styles.scss";

function WbsPage({ project }: Props) {
  const { t } = useTranslation();
  const [areStatusesDisplayed, setAreStatusesDisplayed] = React.useState(false);
  const [hoverStore] = React.useState(createHoverStore);
  const { state, edit } = usePreferences();
  const { wbsFilter, wbsSort: sort } = state;
  const filter: GanttFilter = {
    ...wbsFilter,
    scope: "project",
    projects: [project],
  };
  const setFilter = (nextFilter: GanttFilter) =>
    edit({ ...state, wbsFilter: nextFilter });
  const setSort = (nextSort: S) => edit({ ...state, wbsSort: nextSort });
  const { gantt } = useRetrieveOptimizedGantt(
    filter,
    sort === "zone" ? "ascendingRank" : sort,
    10,
  );
  const {
    isRequestPending: isMetaRequestPending,
    isRequestSuccess: isMetaRequestSuccess,
    isRequestFailure: isMetaRequestFailure,
    meta,
    retrieveWbsMeta,
  } = useRetrieveWbsMeta([project]);

  React.useEffect(() => {
    !isMetaRequestPending &&
      !isMetaRequestSuccess &&
      !isMetaRequestFailure &&
      retrieveWbsMeta({ projects: [project] });
  }, [project.uuid]);

  useMessage(
    ({ type }) =>
      (type === events.moveListElementSucceeded ||
        type === events.editAllocationSucceeded ||
        ["Task", "List", "Milestone", "Event"].find(
          (elementType) =>
            type === `create${elementType}Succeeded` ||
            type === `remove${elementType}Succeeded`,
        )) &&
      retrieveWbsMeta({ projects: [project] }),
  );

  useMessage(
    (event) =>
      (((event.type === events.saveTimeRecordSucceeded ||
        event.type === events.deleteTimeRecordSucceeded) &&
        !!meta[event.data.timeRecord.task.uuid]) ||
        (event.type === events.validateTimeRecordsUpToDateSucceeded &&
          event.data.project.uuid === project.uuid)) &&
      retrieveWbsMeta({ projects: [project] }),
    { localOnly: false },
  );

  return (
    <TreeToggle>
      <div className={styles.listsPage}>
        <Helmet>
          <title>Planzone • {t("projectPage.listsPage.title")}</title>
          <meta name="description" content={project.description} />
        </Helmet>
        {!areStatusesDisplayed && (
          <div className={styles.exportButton}>
            <ExportButton
              project={project}
              filter={filter}
              sort={sort === "zone" ? "ascendingRank" : sort}
            />
          </div>
        )}
        <div className={styles.header}>
          <div className={styles.toolbar}>
            {!areStatusesDisplayed && <Sort sort={sort} select={setSort} />}
            <Filters
              filter={filter}
              setFilter={setFilter}
              areDataTypesDisplayed={!areStatusesDisplayed}
            />
            <DisplayToggle
              isDisplayingStatuses={areStatusesDisplayed}
              toggle={() => setAreStatusesDisplayed(!areStatusesDisplayed)}
            />
          </div>
          <div className={styles.creationMenu}>
            <CreationMenu project={project} />
          </div>
        </div>
        <div className={styles.heading}>
          {!areStatusesDisplayed && <Heading project={project} />}
        </div>
        <div className={styles.main}>
          {!areStatusesDisplayed ? (
            <InfiniteScroll id="wbs" className={styles.wbs} canFetchOnIdle>
              {() => (
                <HoverProvider value={hoverStore}>
                  <Wbs
                    projects={[project]}
                    gantt={gantt}
                    filter={filter}
                    sort={sort === "zone" ? "ascendingRank" : sort}
                    meta={meta}
                    loaderId="wbs"
                    hideProjects
                  />
                </HoverProvider>
              )}
            </InfiniteScroll>
          ) : (
            <div className={styles.statuses}>
              <Statuses project={project} filter={filter} />
            </div>
          )}
        </div>
      </div>
    </TreeToggle>
  );
}

export { WbsPage };
